import { FC, useCallback, useState } from 'react';
import { captureException } from '@sentry/react';
import { Col, Divider, Modal, Row, Tag } from 'antd';
import { AxiosResponse } from 'axios';
import { TbArrowRight, TbCircleFilled, TbInfoCircle, TbTrash } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { PlatformAlert } from '@/shared/components/alerts';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { SetStateMethod } from '@/types';

interface DeleteStageProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    columns: any;
    setColumns: SetStateMethod<any>;
    currentColumn: any;
}

const DeleteStage: FC<DeleteStageProps> = ({ open, setOpen, setColumns, currentColumn }): JSX.Element => {
    const [loading, setLoading] = useState(false);

    const handleColumnPropertiesUpdate = useCallback(async () => {
        if (currentColumn?.id) {
            try {
                setLoading(true);

                await authorizedAxiosInstance
                    .delete(`/api/pipelines/columns/${currentColumn?.id}`)
                    .then((res: AxiosResponse) => {
                        if (res?.status === 200) {
                            setOpen(false);

                            setLoading(false);

                            setColumns((prev: any) => prev.filter((col: any) => col.id !== currentColumn?.id));

                            toast('Stage Removed', {
                                icon: <TbTrash size='1rem' />,
                                description: currentColumn?.name,
                            });
                        }
                    })
                    .finally(() => setLoading(false));
            } catch (error) {
                captureException(error);
            }
        }
    }, [currentColumn, setColumns, setOpen]);

    return (
        <Modal
            open={open}
            mask={false}
            title='Delete Stage'
            okText={
                <>
                    Delete <TbArrowRight style={{ verticalAlign: '-0.15rem' }} />
                </>
            }
            okButtonProps={{
                disabled: loading,
                loading: loading,
                danger: true,
            }}
            onOk={handleColumnPropertiesUpdate}
            onCancel={() => setOpen(false)}
        >
            <Row>
                <Col flex='none'>
                    <TbCircleFilled
                        size='0.5rem'
                        style={{
                            verticalAlign: '0.1rem',
                            marginRight: '0.5rem',
                            color: currentColumn?.color,
                        }}
                    />
                </Col>
                <Col flex='auto'>{currentColumn?.name}</Col>
                <Col flex='none'>
                    <Tag style={{ marginRight: 0, borderColor: 'transparent' }}>
                        <strong>{currentColumn?.cards?.length}</strong> Items in Stage
                    </Tag>
                </Col>
            </Row>
            <Divider />
            <PlatformAlert
                showIcon
                icon={<TbInfoCircle />}
                status='warning'
                title='Items will be removed from this stage'
                description='Any items in this stage will be dissociated from this Pipeline after deletion.'
                style={{
                    marginTop: 0,
                }}
            />
        </Modal>
    );
};

export default DeleteStage;
